import React from 'react';
import { Heart, Sparkles, Quote } from 'lucide-react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../hooks/useLanguage';
export function MyStorySection() {
  const {
    ref,
    isVisible
  } = useScrollReveal();
  const {
    t
  } = useLanguage();
  const brands = ['HBO Max', 'Apple TV+', 'Netflix', 'Disney', 'YouTube'];
  return <Section background="stone" id="my-story" className="py-24" data-id="element-142">
      <div ref={ref} className={`max-w-6xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`} data-id="element-143">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-144">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-fire/10 border border-brand-fire/30 text-brand-fire font-bold uppercase tracking-wider text-sm mb-6" data-id="element-145">
            <Heart className="w-4 h-4" data-id="element-146" />
            <span data-id="element-147">{t('myStory', 'badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-brand-navy mb-4 uppercase leading-tight" data-id="element-148">
            {t('myStory', 'title1')}{' '}
            <span className="text-brand-fire" data-id="element-149">{t('myStory', 'title2')}</span>
          </h2>
          <div className="w-24 h-1 bg-brand-fire mx-auto rounded-full" data-id="element-150"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-151">
          {/* Image Side */}
          <div className="relative" data-id="element-152">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-fire to-brand-amber rounded-2xl rotate-3 opacity-20 blur-sm" data-id="element-153"></div>
            <div className="relative bg-white rounded-2xl p-2 shadow-2xl border border-slate-200" data-id="element-154">
              <img src="https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=800&q=80" alt={t('myStory', 'imageAlt')} className="rounded-xl w-full h-[500px] object-cover" data-id="element-155" />
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-brand-navy text-white font-heading font-bold px-6 py-4 rounded-xl shadow-xl rotate-[-3deg] border-4 border-white" data-id="element-156">
                <div className="text-3xl text-brand-amber leading-none" data-id="element-157">9</div>
                <div className="text-xs uppercase tracking-wider mt-1" data-id="element-158">
                  {t('myStory', 'ageBadge')}
                </div>
              </div>
              {/* Top Sparkle Badge */}
              <div className="absolute -top-4 -right-4 bg-brand-fire text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-1.5" data-id="element-159">
                <Sparkles className="w-4 h-4" data-id="element-160" />
                {t('myStory', 'proBadge')}
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div data-id="element-161">
            <Quote className="w-10 h-10 text-brand-fire/30 mb-4" data-id="element-162" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-163">
              {t('myStory', 'p1Pre')}
              <span className="font-bold text-brand-navy" data-id="element-164">
                {t('myStory', 'p1Highlight')}
              </span>
              {t('myStory', 'p1Post')}
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-6" data-id="element-165">
              {t('myStory', 'p2Pre')}
              <span className="font-bold text-brand-fire" data-id="element-166">
                {t('myStory', 'p2Highlight')}
              </span>
              {t('myStory', 'p2Post')}
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-8" data-id="element-167">
              {t('myStory', 'p3Pre')}
              <span className="font-bold text-brand-navy" data-id="element-168">
                {t('myStory', 'p3Highlight')}
              </span>
              {t('myStory', 'p3Post')}
            </p>

            <a href={t('hero', 'ctaLink')} target="_blank" rel="noopener noreferrer" data-id="element-169">
              <Button variant="primary" size="lg" withIcon data-id="element-170">
                {t('myStory', 'cta')}
              </Button>
            </a>
          </div>
        </div>

        {/* Brand Logos / "As Heard On" */}
        <div className="mt-20" data-id="element-171">
          <p className="text-center text-sm font-bold uppercase tracking-[0.2em] text-slate-500 mb-8" data-id="element-172">
            {t('myStory', 'asHeardOn')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6" data-id="element-173">
            {brands.map(brand => <div key={brand} className="px-6 py-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-id="element-174">
                <span className="font-heading font-bold text-lg md:text-xl text-brand-navy tracking-tight" data-id="element-175">
                  {brand}
                </span>
              </div>)}
          </div>
          <p className="text-center text-sm text-slate-500 italic mt-6 max-w-2xl mx-auto" data-id="element-176">
            {t('myStory', 'asHeardOnNote')}
          </p>
        </div>
      </div>
    </Section>;
}